import React, { FC, useEffect, useRef, useState } from 'react';
import Carousel, { ResponsiveType } from 'react-multi-carousel';

import 'react-multi-carousel/lib/styles.css';

import Left from '@/components/HomePage/Banners/CustomArrow/left';
import Right from '@/components/HomePage/Banners/CustomArrow/right';
import BookOfTheMonth from '@/components/HomePage/BookOfTheMonth';

import { IBestsellerSpotlight } from '@/utils/interfaces/BestsellerSpotlight';

interface Props {
  bestsellerSpotlights: IBestsellerSpotlight[] | undefined;
}

const BookOfTheMonthCarousel: FC<Props> = ({ bestsellerSpotlights }) => {
  const [hover, setHover] = useState<boolean>(false);
  const carouselRef = useRef<any>(null);

  const handleNext = () => {
    if (carouselRef.current) {
      const state = carouselRef.current.state;
      const { currentSlide, slidesToShow, totalItems } = state;
      const nextSlide = Math.min(currentSlide + 1, totalItems - slidesToShow);
      carouselRef.current.goToSlide(nextSlide);
    }
  };

  const handlePrev = () => {
    if (carouselRef.current) {
      const state = carouselRef.current.state;
      const { currentSlide } = state;
      const prevSlide = Math.max(0, Math.ceil(currentSlide - 1));
      carouselRef.current.goToSlide(prevSlide);
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (!hover && bestsellerSpotlights && bestsellerSpotlights.length > 1) {
      interval = setInterval(() => {
        handleNext();
      }, 3000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [hover, bestsellerSpotlights]);

  useEffect(() => {
    if (carouselRef.current) {
      const carousel = carouselRef.current;

      carousel.next = (slidesHavePassed: number = 0) => {
        const { currentSlide, slidesToShow, totalItems } = carousel.state;
        const step = slidesHavePassed > 0 ? slidesHavePassed : 1;
        const nextSlide = Math.min(currentSlide + step, totalItems - slidesToShow);
        carousel.goToSlide(nextSlide);
      };

      carousel.previous = (slidesHavePassed: number = 0) => {
        const { currentSlide } = carousel.state;
        const step = slidesHavePassed > 0 ? slidesHavePassed : 1;
        const prevSlide = Math.max(0, Math.ceil(currentSlide - step));
        carousel.goToSlide(prevSlide);
      };
    }
  }, [bestsellerSpotlights]);

  const CustomDot = ({ onClick, active }: any) => {
    return (
      <button
        onClick={onClick}
        className={` sm:w-4 w-3 sm:h-1 h-0.5 ${active ? 'bg-white' : 'bg-grayZl-850'}`}
      />
    );
  };
  const responsive: ResponsiveType = {
    br1: {
      breakpoint: { max: 10000, min: 1536 },
      items: 1.2,
      slidesToSlide: 1,
    },
    br2: {
      breakpoint: { max: 1536, min: 1280 },
      items: 1.2,
      slidesToSlide: 1,
    },
    br3: {
      breakpoint: { max: 1280, min: 1024 },
      items: 1.1,
      slidesToSlide: 1,
    },
    br4: {
      breakpoint: { max: 1024, min: 768 },
      items: 1,
      slidesToSlide: 1,
    },
    br5: {
      breakpoint: { max: 768, min: 640 },
      items: 1,
      slidesToSlide: 1,
    },
    br6: {
      breakpoint: { max: 640, min: 0 },
      items: 1.08,
      slidesToSlide: 1,
    },
  };
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className='xl:pt-[30px] xl:pb-[30px] lg:pt-[20px] lg:pb-10 md:pb-8 sm:pt-[16px] sm:pb-8 pt-3 pb-6 2xl:px-12 xl:px-12 lg:px-6 md:px-[4px] sm:px-0 px-1'
    >
      <Carousel
        ref={carouselRef}
        autoPlay={false}
        infinite={false}
        rewind={false}
        slidesToSlide={1}
        customDot={<CustomDot />}
        showDots
        arrows
        customRightArrow={<Right show={hover} customNext={handleNext} />}
        customLeftArrow={<Left show={hover} customPrev={handlePrev} />}
        responsive={responsive}
        itemClass='lg:px-2.5 sm:px-[12px] px-2 px-0'
        className='xl:rounded-2xl lg:rounded-[14px] rounded-xl  lg:-ms-2.5 overflow-visible'
        dotListClass='sm:-bottom-[24px] -bottom-[20px] gap-1'
      >
        {bestsellerSpotlights?.map((bestsellerSpotlight) => (
          <BookOfTheMonth
            bestsellerSpotlight={bestsellerSpotlight}
            key={bestsellerSpotlight?._id}
          />
        ))}
      </Carousel>
    </div>
  );
};

export default BookOfTheMonthCarousel;
